import React, { useEffect, useMemo, useState } from "react";
import { getGroupsByDojo } from "../../../services/groupServices";
import { addAdherent } from "../../../services/adherentServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { getDojosByClub } from "../../../services/dojoServices";
import {
  FaCamera,
  FaEnvelope,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaPhone,
  FaPlus,
  FaSave,
  FaTimes,
  FaUser,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const DEFAULT_FORM = {
  nom: "",
  prenom: "",
  email: "",
  tel: "",
  adresse: "",
  cp: "",
  commune: "",
  genre: "Masculin",
  statut_inscription: "Inscrit",
  notes: "",
  date_naissance: "",
  photo_url: "",
};

const CreateAdherentModal = ({
  isOpen,
  onClose,
  onUpdate,
  clubId,
  dojoId = "",
  groupId = "",
}) => {
  const { addNotification } = useNotification();

  const [groups, setGroups] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen || !clubId) return;
    fetchDojos();
  }, [isOpen, clubId]);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(DEFAULT_FORM);
    setGroups([]);
    setErrorMessage("");
    setSaving(false);

    const initialDojo = dojoId ? String(dojoId) : "";
    const initialGroup = groupId ? String(groupId) : "";

    setSelectedDojo(initialDojo);
    setSelectedGroup(initialGroup);

    if (initialDojo) {
      fetchGroups(initialDojo);
    }
  }, [isOpen, dojoId, groupId]);

  const fetchDojos = async () => {
    if (!clubId) return;

    try {
      const data = await getDojosByClub(clubId);
      setDojos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des dojos :", error);
      setDojos([]);
      addNotification("Erreur lors du chargement des lieux.", "error");
    }
  };

  const fetchGroups = async (currentDojoId) => {
    try {
      const data = await getGroupsByDojo(currentDojoId);
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des groupes :", error);
      setGroups([]);
      addNotification("Erreur lors du chargement des groupes.", "error");
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const previewImage = useMemo(() => {
    if (formData.photo_url) return formData.photo_url;
    if (formData.genre === "Masculin") return "/img/judoka_avatar.png";
    if (formData.genre === "Féminin") return "/img/judokate_avatar.png";
    return "/img/default-avatar.png";
  }, [formData.photo_url, formData.genre]);

  const selectedDojoLabel =
    dojos.find((dojo) => String(dojo.id) === String(selectedDojo))?.nom_dojo ||
    "Aucun lieu";

  const selectedGroupLabel =
    groups.find((group) => String(group.id) === String(selectedGroup))
      ?.nom_groupe || "Aucun groupe";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!clubId) {
      setErrorMessage("Aucun club sélectionné.");
      return;
    }

    if (!formData.nom.trim() || !formData.prenom.trim()) {
      setErrorMessage("Le nom et le prénom sont obligatoires.");
      return;
    }

    if (!selectedDojo) {
      setErrorMessage("Veuillez sélectionner un lieu.");
      return;
    }

    if (!selectedGroup) {
      setErrorMessage("Veuillez sélectionner un groupe.");
      return;
    }

    try {
      setSaving(true);

      await addAdherent({
        ...formData,
        group_id: Number(selectedGroup),
      });

      addNotification("Adhérent ajouté avec succès.", "success");

      if (onUpdate) {
        await onUpdate();
      }

      onClose();
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'adhérent :", error);
      setErrorMessage("Erreur lors de l'ajout de l'adhérent.");
      addNotification("Erreur lors de l'ajout de l'adhérent.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[220] bg-slate-950/75 backdrop-blur-sm">
      <div className="flex h-[100dvh] items-stretch justify-center p-0 sm:p-4">
        <div className="flex h-[100dvh] w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-[28px] sm:border sm:border-white/10 dark:sm:border-slate-700">
          {/* Header fixe */}
          <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                  Adhérent
                </p>
                <h2 className="mt-1 text-xl font-black text-slate-900 dark:text-slate-100 sm:text-2xl">
                  Ajouter un adhérent
                </h2>
              </div>

              <button
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Zone scrollable */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid min-h-full grid-cols-1 lg:grid-cols-[0.9fr_1.1fr]">
              {/* Colonne gauche */}
              <div className="border-b border-white/10 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-5 text-white lg:border-b-0 lg:border-r lg:border-white/10 sm:p-6">
                <p className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  Aperçu
                </p>

                <div className="mt-6 flex flex-col items-center rounded-[20px] border border-white/10 bg-white/10 p-4 text-center">
                  <img
                    src={previewImage}
                    alt="Photo adhérent"
                    className="h-24 w-24 rounded-full object-cover shadow-xl sm:h-28 sm:w-28"
                  />

                  <h3 className="mt-4 text-lg font-bold sm:text-xl">
                    {formData.prenom || "Prénom"} {formData.nom || "Nom"}
                  </h3>

                  <p className="mt-2 text-sm text-white/75">
                    {formData.email || "Email"} · {formData.tel || "Téléphone"}
                  </p>

                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white">
                      {formData.statut_inscription || "Statut"}
                    </span>
                    <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white">
                      {formData.genre || "Genre"}
                    </span>
                  </div>
                </div>

                <div className="mt-5 space-y-3">
                  <div className="rounded-2xl border border-white/10 bg-white/10 p-4 text-left">
                    <div className="flex items-start gap-3">
                      <FaMapMarkerAlt className="mt-1 text-white/70" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-white/60">
                          Lieu
                        </p>
                        <p className="mt-1 text-sm font-semibold text-white">
                          {selectedDojoLabel}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/10 p-4 text-left">
                    <div className="flex items-start gap-3">
                      <FaLayerGroup className="mt-1 text-white/70" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-white/60">
                          Groupe
                        </p>
                        <p className="mt-1 text-sm font-semibold text-white">
                          {selectedGroupLabel}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Formulaire */}
              <div className="p-4 sm:p-6">
                {errorMessage && (
                  <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                    {errorMessage}
                  </div>
                )}

                <form id="create-adherent-form" onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Lieu
                      </label>
                      <select
                        value={selectedDojo}
                        onChange={(e) => {
                          setSelectedDojo(e.target.value);
                          setSelectedGroup("");
                          fetchGroups(e.target.value);
                        }}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                      >
                        <option value="">Sélectionner un lieu</option>
                        {dojos.map((dojo) => (
                          <option key={dojo.id} value={dojo.id}>
                            {dojo.nom_dojo}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Groupe
                      </label>
                      <select
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        disabled={!selectedDojo}
                        className={cn(
                          "w-full rounded-2xl border px-4 py-3 text-sm outline-none transition",
                          !selectedDojo
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                            : "border-slate-200 bg-white text-slate-900 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                        )}
                      >
                        <option value="">Sélectionner un groupe</option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.nom_groupe}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Nom
                      </label>
                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Prénom
                      </label>
                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Email
                      </label>
                      <div className="relative">
                        <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Téléphone
                      </label>
                      <div className="relative">
                        <FaPhone className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          name="tel"
                          value={formData.tel}
                          onChange={handleChange}
                          className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Adresse
                    </label>
                    <div className="relative">
                      <FaMapMarkerAlt className="pointer-events-none absolute left-4 top-4 text-slate-400" />
                      <textarea
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        rows={3}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pl-11 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      type="text"
                      name="cp"
                      placeholder="Code postal"
                      value={formData.cp}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                    />

                    <input
                      type="text"
                      name="commune"
                      placeholder="Commune"
                      value={formData.commune}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Genre
                      </label>
                      <select
                        name="genre"
                        value={formData.genre}
                        onChange={handleChange}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                      >
                        <option value="Masculin">Masculin</option>
                        <option value="Féminin">Féminin</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Date de naissance
                      </label>
                      <input
                        type="date"
                        name="date_naissance"
                        value={formData.date_naissance}
                        onChange={handleChange}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Statut d'inscription
                    </label>
                    <select
                      name="statut_inscription"
                      value={formData.statut_inscription}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                    >
                      <option value="Inscrit">Inscrit</option>
                      <option value="Cours d'Essai">Cours d'Essai</option>
                      <option value="Licencié">Licencié</option>
                      <option value="Licencié hors club">Licencié hors club</option>
                      <option value="Non inscrit">Non inscrit</option>
                      <option value="Arrêt">Arrêt</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Photo (URL)
                    </label>
                    <div className="relative">
                      <FaCamera className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        name="photo_url"
                        value={formData.photo_url}
                        onChange={handleChange}
                        className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={4}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Footer fixe */}
          <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                form="create-adherent-form"
                disabled={saving}
                className={cn(
                  "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                  saving
                    ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                    : "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                )}
              >
                <FaSave />
                {saving ? "Ajout..." : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAdherentModal;